import { useEffect, useId, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowUpRight,
  BadgeDollarSign,
  BarChart3,
  CreditCard,
  ExternalLink,
  FileText,
  Gauge,
  Coins,
  Loader2,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useProviderCustomers, useProviderCustomer } from '@/hooks/provider/useProviderCustomers';
import { useSubscriptionPlanOptions } from '@/hooks/provider/useSubscriptionPlanOptions';
import {
  normalizePlanOptions,
  selectAvailableCycles,
  selectIsWorkspaceRequired,
  validateWorkspaceName,
} from '@/lib/provider/plan-change';
import { extractErrorStatus, extractJsonApiErrorMessage, extractJsonApiFieldErrors } from '@/lib/jsonapi/errors';
import { analyticsService } from '@/services/analytics.service';
import { type SubscriptionPlanOption } from '@/services/provider/subscription-plan-options.service';
import {
  useChangeSubscriptionPlan,
  useIssuePayLink,
  usePreviewInvoices,
  usePreviewPaymentMethod,
  useSelectCustomerSubscription,
  usePreviewSubscription,
  usePreviewUsageRecords,
  usePreviewUsageSummary,
  type UsageRecordsQuery,
} from '@/hooks/provider/useCustomerPreview';
import { useIssueCustomerPortalLink } from '@/hooks/provider/useProviderApiTokens';
import { toast } from 'sonner';
import {
  UsageMetric,
  InvoiceRecord,
  PaymentMethodSummary,
  ProviderSubscriptionSummary,
  UsageSummary,
  type UsageStatus,
} from '@/types/provider-customer-preview';

function formatMoney(amount: number | null | undefined, currency?: string | null, isMinor = false) {
  if (amount === null || amount === undefined) return '—';
  const value = isMinor ? amount / 100 : amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency ?? 'USD'}`;
  }
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatNumber(value: number | null | undefined, suffix?: string) {
  if (value === null || value === undefined) return '—';
  return `${value.toLocaleString()}${suffix ? ` ${suffix}` : ''}`;
}

const statusColorClasses: Record<string, string> = {
  success: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  info: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  warning: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  danger: 'bg-red-500/10 text-red-500 border-red-500/30',
  secondary: 'bg-muted text-muted-foreground border-transparent',
};

const usageStatusClasses: Record<string, string> = {
  within: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  nearing: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  exceeded: 'bg-red-500/10 text-red-500 border-red-500/30',
  unlimited: 'bg-muted text-muted-foreground border-transparent',
};

const metricLabelMap: Record<string, { key: string; fallback: string }> = {
  api_call: { key: 'customerPreview.metrics.apiCalls', fallback: 'API calls' },
  storage: { key: 'customerPreview.metrics.storage', fallback: 'Storage' },
  seats: { key: 'customerPreview.metrics.seats', fallback: 'Seats' },
  credit_deduction: { key: 'customerPreview.metrics.creditDeduction', fallback: 'Credits deducted' },
};

const usageStatusLabelMap: Record<UsageStatus, { key: string; fallback: string }> = {
  within: { key: 'customerPreview.usageStatus.within', fallback: 'Within' },
  nearing: { key: 'customerPreview.usageStatus.nearing', fallback: 'Nearing limit' },
  exceeded: { key: 'customerPreview.usageStatus.exceeded', fallback: 'Exceeded' },
  unlimited: { key: 'customerPreview.usageStatus.unlimited', fallback: 'Unlimited' },
};

const getMetricLabel = (t: TFunction, key: string) => {
  const entry = metricLabelMap[key];
  return entry ? t(entry.key, entry.fallback) : key;
};

const getUsageStatusLabel = (t: TFunction, status: UsageStatus) => {
  const entry = usageStatusLabelMap[status];
  return entry ? t(entry.key, entry.fallback) : status;
};

const getSubscriptionStatusLabel = (t: TFunction, subscription: ProviderSubscriptionSummary) => {
  const rawStatusLabel = subscription.statusLabel ?? subscription.status ?? '';
  const fallbackLabel = rawStatusLabel.split(/[./]/).pop()?.replace(/_/g, ' ') ?? rawStatusLabel;
  return t(rawStatusLabel, { defaultValue: fallbackLabel });
};

const customerStatusClasses: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  inactive: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
  trial: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  suspended: 'bg-red-500/10 text-red-500 border-red-500/30',
  pending_verification: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
};

const WORKSPACE_GATE_ENABLED = import.meta.env.VITE_BILLING_PLAN_SWITCH_WORKSPACE_GATE !== 'false';
type PlanTypeFilter = 'all' | 'individual' | 'team';

export default function CustomerPreviewPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerIdFromQuery = searchParams.get('customerId') ?? undefined;
  const { data: customersResponse, isLoading: customersLoading, refetch: refetchCustomers } = useProviderCustomers({
    perPage: 20,
  });
  const customers = customersResponse?.customers ?? [];
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(customerIdFromQuery);

  useEffect(() => {
    if (customerIdFromQuery && customerIdFromQuery !== selectedCustomerId) {
      setSelectedCustomerId(customerIdFromQuery);
    }
  }, [customerIdFromQuery, selectedCustomerId]);

  const handleSelectCustomer = (id: string | undefined) => {
    setSelectedCustomerId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) {
        next.set('customerId', id);
      } else {
        next.delete('customerId');
      }
      return next;
    });
  };

  useEffect(() => {
    if (!customerIdFromQuery && !selectedCustomerId && customers.length > 0) {
      setSelectedCustomerId(customers[0].id);
    }
  }, [customerIdFromQuery, customers, selectedCustomerId]);

  const { data: customerDetail } = useProviderCustomer(selectedCustomerId);
  const {
    data: subscriptionPreview,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } =
    usePreviewSubscription(selectedCustomerId);
  const subscription = subscriptionPreview?.subscription ?? undefined;
  const subscriptionMeta = subscriptionPreview?.meta;
  const { data: usageSummary, isLoading: usageLoading, error: usageError, refetch: refetchUsage } =
    usePreviewUsageSummary(selectedCustomerId);
  const [metricFilter, setMetricFilter] = useState<string | undefined>();
  const [usagePage, setUsagePage] = useState(1);
  useEffect(() => setUsagePage(1), [metricFilter, selectedCustomerId]);

  const usageRecordsParams: UsageRecordsQuery | undefined = selectedCustomerId
    ? {
        customerId: selectedCustomerId,
        metric: metricFilter,
        page: usagePage,
        perPage: 10,
        sort: '-recorded_at',
      }
    : undefined;
  const { data: usageRecords, isFetching: usageRecordsLoading } = usePreviewUsageRecords(usageRecordsParams);
  const { data: invoices, isLoading: invoicesLoading } = usePreviewInvoices(selectedCustomerId);
  const { data: paymentMethod, isLoading: paymentLoading } = usePreviewPaymentMethod(selectedCustomerId);

  const issuePortalLink = useIssueCustomerPortalLink();
  const issuePayLink = useIssuePayLink();
  const changePlan = useChangeSubscriptionPlan();
  const selectSubscription = useSelectCustomerSubscription();

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const planIdFieldId = useId();
  const planTypeFilterFieldId = useId();
  const cycleFieldId = useId();
  const workspaceFieldId = useId();
  const [planTypeFilter, setPlanTypeFilter] = useState<PlanTypeFilter>('all');
  const [planIdInput, setPlanIdInput] = useState('');
  const [planCycleId, setPlanCycleId] = useState<string>('');
  const [workspaceNameInput, setWorkspaceNameInput] = useState('');
  const [formAttempted, setFormAttempted] = useState(false);
  const [workspaceRequirementTrackedFor, setWorkspaceRequirementTrackedFor] = useState<string | null>(null);
  const [planFormErrors, setPlanFormErrors] = useState<{
    planId?: string;
    billingCycleId?: string;
    workspaceName?: string;
    global?: string;
  }>({});
  const {
    data: fetchedPlanOptions = [],
    isLoading: planOptionsLoading,
    refetch: refetchPlanOptions,
  } = useSubscriptionPlanOptions(
    { customerId: selectedCustomerId },
    (planDialogOpen || (subscription === undefined && subscriptionMeta?.canSelectSubscription === true)) &&
      Boolean(selectedCustomerId),
  );
  const planOptions = useMemo<SubscriptionPlanOption[]>(
    () =>
      (subscriptionMeta?.planOptions && subscriptionMeta.planOptions.length > 0
        ? subscriptionMeta.planOptions
        : fetchedPlanOptions) as SubscriptionPlanOption[],
    [fetchedPlanOptions, subscriptionMeta?.planOptions],
  );
  const planStore = useMemo(
    () => normalizePlanOptions(planOptions, subscription?.plan?.id),
    [planOptions, subscription?.plan?.id],
  );
  const selectedPlanOption = useMemo(
    () => (planIdInput ? planStore.plansById[planIdInput] ?? null : null),
    [planIdInput, planStore.plansById],
  );
  const resolvePlanAudience = (option: SubscriptionPlanOption): Exclude<PlanTypeFilter, 'all'> => {
    const normalizedType = (option.planType ?? '').toLowerCase();
    if (normalizedType.includes('team')) {
      return 'team';
    }
    if (normalizedType.includes('individual')) {
      return 'individual';
    }
    return option.isTeamPlan ? 'team' : 'individual';
  };
  const isPlanMatchingFilter = (option: SubscriptionPlanOption): boolean => {
    if (planTypeFilter === 'all') return true;
    return resolvePlanAudience(option) === planTypeFilter;
  };
  const filteredPlanGroups = useMemo(
    () =>
      planStore.rootPlans
        .map((rootPlan) => {
          const rootMatches = isPlanMatchingFilter(rootPlan);
          const variants = (planStore.variantsByParentId[rootPlan.id] ?? []).filter(isPlanMatchingFilter);
          if (!rootMatches && variants.length === 0) {
            return null;
          }
          return {
            rootPlan,
            rootMatches,
            variants,
          };
        })
        .filter((entry): entry is { rootPlan: SubscriptionPlanOption; rootMatches: boolean; variants: SubscriptionPlanOption[] } => Boolean(entry)),
    [planStore.rootPlans, planStore.variantsByParentId, planTypeFilter],
  );
  const selectedPlanCycles = useMemo(
    () => selectAvailableCycles(planStore, planIdInput),
    [planIdInput, planStore],
  );
  const selectedIsCurrentSelection = useMemo(
    () =>
      Boolean(
        selectedPlanCycles.find((cycle) => cycle.billingCycleId === planCycleId)?.isCurrentSelection,
      ),
    [selectedPlanCycles, planCycleId],
  );
  const workspaceGateEnabledForAccount = WORKSPACE_GATE_ENABLED;
  const isWorkspaceRequired = useMemo(
    () => selectIsWorkspaceRequired(planStore, planIdInput, workspaceGateEnabledForAccount),
    [planIdInput, planStore, workspaceGateEnabledForAccount],
  );
  const workspaceValidationCode = isWorkspaceRequired ? validateWorkspaceName(workspaceNameInput) : null;

  const getWorkspaceValidationMessage = (code: string | null) => {
    if (code === 'required') {
      return t('customerPreview.workspaceNameRequired', 'Workspace name is required.');
    }
    if (code === 'min') {
      return t('customerPreview.workspaceNameMinLength', 'Workspace name must be at least 2 characters.');
    }
    if (code === 'max') {
      return t('customerPreview.workspaceNameMaxLength', 'Workspace name must be 255 characters or less.');
    }
    return undefined;
  };

  const resetPlanChangeForm = () => {
    setPlanTypeFilter('all');
    setPlanIdInput('');
    setPlanCycleId('');
    setWorkspaceNameInput('');
    setPlanFormErrors({});
    setFormAttempted(false);
    setWorkspaceRequirementTrackedFor(null);
  };

  const currentPlanType = useMemo(() => {
    const currentOption = planStore.currentPlanId ? planStore.plansById[planStore.currentPlanId] : null;
    return currentOption?.planType ?? null;
  }, [planStore.currentPlanId, planStore.plansById]);

  const getSelectedBillingCycleName = () =>
    selectedPlanCycles.find((cycle) => cycle.billingCycleId === planCycleId)?.billingCycleName ??
    planCycleId;

  useEffect(() => {
    if (!planDialogOpen || !planIdInput || !isWorkspaceRequired || workspaceRequirementTrackedFor === planIdInput) {
      return;
    }
    analyticsService.track('workspace_name_required_shown', {
      customer_id: selectedCustomerId,
      to_plan_id: planIdInput,
      workspace_required: true,
    });
    setWorkspaceRequirementTrackedFor(planIdInput);
  }, [isWorkspaceRequired, planDialogOpen, planIdInput, selectedCustomerId, workspaceRequirementTrackedFor]);

  useEffect(() => {
    if (!planIdInput || !selectedPlanOption) return;
    if (isPlanMatchingFilter(selectedPlanOption)) return;
    setPlanIdInput('');
    setPlanCycleId('');
    setWorkspaceNameInput('');
    setPlanFormErrors((prev) => ({
      ...prev,
      planId: undefined,
      billingCycleId: undefined,
      workspaceName: undefined,
    }));
  }, [planTypeFilter, planIdInput, selectedPlanOption]);

  const usageMetricsOptions = useMemo(() => {
    if (!usageSummary?.metrics) return [];
    return Object.keys(usageSummary.metrics);
  }, [usageSummary?.metrics]);

  const handleIssuePortal = async () => {
    if (!selectedCustomerId) return;
    try {
      const result = await issuePortalLink.mutateAsync({ customerId: selectedCustomerId });
      if (result.url) {
        window.open(result.url, '_blank', 'noopener');
      }
      toast.success(
        t('customers.detail.ssoLinkIssued', 'Customer portal opened in a new tab.'),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('customers.detail.ssoLinkError', 'Unable to issue portal link. Try again later.');
      toast.error(message);
    }
  };

  const handlePayLink = async () => {
    if (!subscription?.id) return;
    try {
      const response = await issuePayLink.mutateAsync({ subscriptionId: subscription.id });
      if (response.url) {
        window.open(response.url, '_blank', 'noopener');
      }
      toast.success(t('customerPreview.payLinkCreated', 'Pay link created'));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('customerPreview.payLinkError', 'Unable to create pay link');
      toast.error(message);
    }
  };

  const handlePlanDialogOpenChange = (open: boolean) => {
    setPlanDialogOpen(open);
    if (open) {
      analyticsService.track('plan_change_modal_opened', {
        customer_id: selectedCustomerId,
        from_plan_id: planStore.currentPlanId ?? subscription?.plan?.id ?? null,
        from_plan_type: currentPlanType,
        workspace_gate_enabled: workspaceGateEnabledForAccount,
      });
    }
    if (!open) {
      resetPlanChangeForm();
    }
  };

  const handlePlanSelect = (nextPlanId: string) => {
    setPlanIdInput(nextPlanId);
    setPlanCycleId('');
    setWorkspaceNameInput('');
    setPlanFormErrors({});
    setFormAttempted(false);

    const selected = planStore.plansById[nextPlanId];
    analyticsService.track('plan_selected', {
      customer_id: selectedCustomerId,
      from_plan_id: planStore.currentPlanId ?? subscription?.plan?.id ?? null,
      from_plan_type: currentPlanType,
      to_plan_id: selected?.id ?? nextPlanId,
      to_plan_type: selected?.planType ?? null,
      workspace_required: Boolean(selected?.requiresWorkspaceNameForSwitch && workspaceGateEnabledForAccount),
    });
  };

  const handlePlanChange = async () => {
    if (!subscription?.id && !selectedCustomerId) {
      return;
    }

    setFormAttempted(true);
    const nextErrors: { planId?: string; billingCycleId?: string; workspaceName?: string; global?: string } = {};

    if (!planIdInput) {
      nextErrors.planId = t('customerPreview.planIdRequired', 'Plan is required');
    }
    if (!planCycleId) {
      nextErrors.billingCycleId = t('customerPreview.planCycleRequired', 'Billing cycle is required');
    }
    if (planIdInput && selectedPlanCycles.length === 0) {
      nextErrors.billingCycleId = t(
        'customerPreview.planCycleUnavailable',
        'No billing cycles are available for the selected plan.',
      );
    }
    if (selectedIsCurrentSelection) {
      nextErrors.billingCycleId = t(
        'customerPreview.planAlreadyCurrent',
        'The selected plan is already active.',
      );
    }
    if (isWorkspaceRequired) {
      const workspaceError = getWorkspaceValidationMessage(workspaceValidationCode);
      if (workspaceError) {
        nextErrors.workspaceName = workspaceError;
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setPlanFormErrors(nextErrors);
      return;
    }

    setPlanFormErrors({});
    analyticsService.track('plan_change_submitted', {
      customer_id: selectedCustomerId,
      from_plan_id: planStore.currentPlanId ?? subscription?.plan?.id ?? null,
      from_plan_type: currentPlanType,
      to_plan_id: planIdInput,
      to_plan_type: selectedPlanOption?.planType ?? null,
      billing_cycle_id: planCycleId,
      workspace_required: isWorkspaceRequired,
      mode: subscription?.id ? 'change' : 'create',
    });

    try {
      if (subscription?.id) {
        await changePlan.mutateAsync({
          subscriptionId: subscription.id,
          planId: planIdInput,
          billingCycleId: planCycleId || undefined,
          workspaceName: isWorkspaceRequired ? workspaceNameInput.trim() : undefined,
          customerId: selectedCustomerId,
        });
      } else {
        await selectSubscription.mutateAsync({
          customerId: selectedCustomerId as string,
          planId: planIdInput,
        });
      }

      analyticsService.track('plan_change_succeeded', {
        customer_id: selectedCustomerId,
        from_plan_id: planStore.currentPlanId ?? subscription?.plan?.id ?? null,
        from_plan_type: currentPlanType,
        to_plan_id: planIdInput,
        to_plan_type: selectedPlanOption?.planType ?? null,
        billing_cycle_id: planCycleId,
        workspace_required: isWorkspaceRequired,
        mode: subscription?.id ? 'change' : 'create',
      });
      toast.success(
        t('customerPreview.planChangeSuccessWithCycle', 'Plan changed to {{plan}} ({{cycle}})', {
          plan: selectedPlanOption?.name ?? planIdInput,
          cycle: getSelectedBillingCycleName(),
        }),
      );
      handlePlanDialogOpenChange(false);
      await Promise.all([refetchSubscription(), refetchUsage(), refetchPlanOptions()]);
    } catch (error) {
      const status = extractErrorStatus(error);
      const fieldErrors = extractJsonApiFieldErrors(error);
      const message = extractJsonApiErrorMessage(
        error,
        t('customerPreview.planChangeError', 'Unable to change plan'),
      );
      const mergedErrors: { planId?: string; billingCycleId?: string; workspaceName?: string; global?: string } = {};

      if (fieldErrors.planId) mergedErrors.planId = fieldErrors.planId;
      if (fieldErrors.billingCycleId) mergedErrors.billingCycleId = fieldErrors.billingCycleId;
      if (fieldErrors.workspaceName) mergedErrors.workspaceName = fieldErrors.workspaceName;

      if (!mergedErrors.workspaceName) {
        const normalizedMessage = message.toLowerCase();
        if (normalizedMessage.includes('workspace') && normalizedMessage.includes('required')) {
          mergedErrors.workspaceName = message;
        }
      }

      if (status === 404) {
        mergedErrors.global = t(
          'customerPreview.subscriptionNotFound',
          'Subscription was not found. Refresh customer context and try again.',
        );
      }

      setPlanFormErrors((prev) => ({ ...prev, ...mergedErrors }));

      analyticsService.track('plan_change_failed', {
        customer_id: selectedCustomerId,
        from_plan_id: planStore.currentPlanId ?? subscription?.plan?.id ?? null,
        from_plan_type: currentPlanType,
        to_plan_id: planIdInput,
        to_plan_type: selectedPlanOption?.planType ?? null,
        billing_cycle_id: planCycleId,
        workspace_required: isWorkspaceRequired,
        status: status ?? null,
        mode: subscription?.id ? 'change' : 'create',
      });

      if (status === 404) {
        toast.error(
          t('customerPreview.subscriptionNotFoundToast', 'Subscription not found. Refresh and try again.'),
        );
        return;
      }

      toast.error(message);
    }
  };

  const renderPlanOption = (option: SubscriptionPlanOption, isVariantRow = false) => {
    const audience = resolvePlanAudience(option);
    const suffix: string[] = [];
    if (option.isVariant) {
      suffix.push(t('customerPreview.variantBadge', 'Variant'));
    }
    suffix.push(audience === 'team' ? t('customerPreview.planTypeFilterTeam', 'Team') : t('customerPreview.planTypeFilterIndividual', 'Individual'));

    const label = `${option.name || option.id}${suffix.length > 0 ? ` (${suffix.join(' · ')})` : ''}`;

    return (
      <SelectItem key={option.id} value={option.id} textValue={label}>
        <span className={isVariantRow ? 'pl-3' : ''}>{label}</span>
      </SelectItem>
    );
  };

  const showWorkspaceError = isWorkspaceRequired && (formAttempted || Boolean(planFormErrors.workspaceName));
  const workspaceErrorMessage = planFormErrors.workspaceName ?? getWorkspaceValidationMessage(workspaceValidationCode);
  const disablePlanSubmit =
    changePlan.isPending ||
    selectSubscription.isPending ||
    (!subscription?.id && !(selectedCustomerId && subscriptionMeta?.canSelectSubscription)) ||
    !planIdInput ||
    !planCycleId ||
    selectedPlanCycles.length === 0 ||
    selectedIsCurrentSelection ||
    (isWorkspaceRequired && Boolean(workspaceValidationCode));

  const renderEmptyState = (
    <Card>
      <CardContent className="py-10 text-center text-sm text-muted-foreground">
        {customersLoading
          ? t('customerPreview.loadingCustomers', 'Loading customers…')
          : t('customerPreview.noCustomerSelected', 'Select a customer to preview usage and billing.')}
      </CardContent>
    </Card>
  );

  return (
    <ProviderLayout>
      <div className="container mx-auto space-y-6 px-6 py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              {t('products.customerPreviewDescription', 'See how your billing options will appear to customers')}
            </p>
            <h1 className="text-3xl font-bold tracking-tight">{t('products.customerPreview', 'Customer Preview')}</h1>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select
              value={selectedCustomerId ?? ''}
              onValueChange={(value) => handleSelectCustomer(value || undefined)}
              disabled={customersLoading}
            >
              <SelectTrigger className="min-w-[240px]">
                <SelectValue placeholder={t('customerPreview.selectCustomer', 'Select customer')} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{customer.name}</span>
                      <span className="text-xs text-muted-foreground">{customer.email}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetchCustomers()}
              disabled={customersLoading}
              title={t('customerPreview.refreshCustomers', 'Refresh customers')}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="default"
              className="gap-2"
              disabled={!selectedCustomerId || issuePortalLink.isPending}
              onClick={handleIssuePortal}
            >
              {issuePortalLink.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {t('customers.detail.openPortal', 'Open customer portal')}
            </Button>
          </div>
        </div>

        {!selectedCustomerId ? (
          renderEmptyState
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-3">
              <div className="flex flex-col justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('customerPreview.previewingCustomer', 'Previewing customer')}
                  </div>
                  <div className="text-lg font-semibold">
                    {customerDetail?.name ?? t('customers.detail.loadingName', 'Customer')}
                  </div>
                  <div className="text-sm text-muted-foreground">{customerDetail?.email ?? '—'}</div>
                </div>
                {customerDetail ? (
                  <Badge variant="outline" className={customerStatusClasses[customerDetail.status] ?? ''}>
                    {customerDetail.statusLabel ?? customerDetail.status}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="space-y-6 lg:col-span-2">
              <PlanCard
                subscription={subscription}
                canSelectSubscription={Boolean(subscriptionMeta?.canSelectSubscription)}
                isLoading={subscriptionLoading}
                error={subscriptionError instanceof Error ? subscriptionError.message : undefined}
                onRetry={refetchSubscription}
                onChangePlan={() => handlePlanDialogOpenChange(true)}
                onSendPayLink={handlePayLink}
                payLinkPending={issuePayLink.isPending}
              />

              <UsageCard
                usageSummary={usageSummary}
                isLoading={usageLoading}
                error={usageError instanceof Error ? usageError.message : undefined}
                onRetry={refetchUsage}
              />

              <UsageRecordsTable
                usageSummary={usageSummary}
                usageRecords={usageRecords}
                isLoading={usageRecordsLoading}
                metricFilter={metricFilter}
                onMetricChange={(value) => setMetricFilter(value || undefined)}
                page={usagePage}
                onPageChange={setUsagePage}
                availableMetrics={usageMetricsOptions}
              />
            </div>

            <div className="space-y-6">
              <InvoicesCard invoices={invoices?.data} isLoading={invoicesLoading} />
              <PaymentMethodCard paymentMethod={paymentMethod} isLoading={paymentLoading} />
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <InfoDotIcon />
                    {t('customerPreview.safetyNotes', 'Safety notes')}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      'customerPreview.safetyNotesDescription',
                      'Keep portal links and pay links limited-use. Avoid exposing Stripe IDs or metadata directly.',
                    )}
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        )}
      </div>

      <Dialog open={planDialogOpen} onOpenChange={handlePlanDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('customerPreview.changePlanTitle', 'Change plan')}</DialogTitle>
            <DialogDescription>
              {t(
                'customerPreview.changePlanDescription',
                'Select a target plan and billing cycle to switch this subscription.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {planFormErrors.global ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('customerPreview.subscriptionNotFoundTitle', 'Subscription not found')}</AlertTitle>
                <AlertDescription>{planFormErrors.global}</AlertDescription>
              </Alert>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor={planTypeFilterFieldId}>
                {t('customerPreview.planTypeFilterLabel', 'Plan type')}
              </Label>
              <Select
                value={planTypeFilter}
                onValueChange={(value) => setPlanTypeFilter(value as PlanTypeFilter)}
              >
                <SelectTrigger id={planTypeFilterFieldId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('customerPreview.planTypeFilterAll', 'All plans')}</SelectItem>
                  <SelectItem value="individual">{t('customerPreview.planTypeFilterIndividual', 'Individual')}</SelectItem>
                  <SelectItem value="team">{t('customerPreview.planTypeFilterTeam', 'Team')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={planIdFieldId}>{t('customerPreview.planIdLabel', 'Plan')}</Label>
              <Select value={planIdInput} onValueChange={handlePlanSelect}>
                <SelectTrigger id={planIdFieldId}>
                  <SelectValue placeholder={t('customerPreview.planSelectPlaceholder', 'Select a plan')} />
                </SelectTrigger>
                <SelectContent>
                  {planOptionsLoading ? (
                    <SelectItem value="loading" disabled>
                      {t('customerPreview.planSelectLoading', 'Loading plans...')}
                    </SelectItem>
                  ) : filteredPlanGroups.length === 0 ? (
                    <SelectItem value="empty" disabled>
                      {t('customerPreview.planSelectEmpty', 'No plans available')}
                    </SelectItem>
                  ) : (
                    filteredPlanGroups.map(({ rootPlan, rootMatches, variants }) => {
                      if (variants.length === 0) {
                        return renderPlanOption(rootPlan);
                      }

                      return (
                        <SelectGroup key={rootPlan.id}>
                          <SelectLabel>{rootPlan.name || rootPlan.id}</SelectLabel>
                          {rootMatches ? renderPlanOption(rootPlan) : null}
                          {variants.map((variant) => renderPlanOption(variant, true))}
                        </SelectGroup>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {planFormErrors.planId ? (
                <p className="text-xs text-destructive">{planFormErrors.planId}</p>
              ) : null}
              {selectedPlanOption ? (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {selectedIsCurrentSelection ? (
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {t('customerPreview.currentBadge', 'Current')}
                    </Badge>
                  ) : null}
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {resolvePlanAudience(selectedPlanOption) === 'team'
                      ? t('customerPreview.planTypeFilterTeam', 'Team')
                      : t('customerPreview.planTypeFilterIndividual', 'Individual')}
                  </Badge>
                  {selectedPlanOption.isVariant ? (
                    <Badge variant="secondary" className="text-[10px] uppercase">
                      {t('customerPreview.variantBadge', 'Variant')}
                    </Badge>
                  ) : null}
                  {selectedPlanOption.pricing?.length ? (
                    <span className="text-muted-foreground">
                      {selectedPlanOption.pricing
                        .map((pricing) => pricing.billingCycleName || pricing.billingCycleId)
                        .join(', ')}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor={cycleFieldId}>{t('customerPreview.planCycleLabel', 'Billing cycle')}</Label>
              <Select
                value={planCycleId}
                onValueChange={(value) => {
                  setPlanCycleId(value);
                  if (planFormErrors.billingCycleId) {
                    setPlanFormErrors((prev) => ({ ...prev, billingCycleId: undefined }));
                  }
                }}
                disabled={selectedPlanCycles.length === 0}
              >
                <SelectTrigger id={cycleFieldId}>
                  <SelectValue
                    placeholder={t('customerPreview.planCyclePlaceholder', 'Select a billing cycle')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {selectedPlanCycles.length === 0 ? (
                    <SelectItem value="empty" disabled>
                      {t('customerPreview.planCycleEmpty', 'No billing cycles available')}
                    </SelectItem>
                  ) : (
                    selectedPlanCycles.map((pricing) => (
                      <SelectItem key={pricing.billingCycleId} value={pricing.billingCycleId}>
                        {pricing.billingCycleName || pricing.billingCycleId}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {planFormErrors.billingCycleId ? (
                <p className="text-xs text-destructive">{planFormErrors.billingCycleId}</p>
              ) : null}
            </div>
            {isWorkspaceRequired ? (
              <div className="space-y-2">
                <Label htmlFor={workspaceFieldId}>{t('customerPreview.workspaceNameLabel', 'Workspace name')}</Label>
                <Input
                  id={workspaceFieldId}
                  value={workspaceNameInput}
                  onChange={(event) => {
                    setWorkspaceNameInput(event.target.value);
                    if (planFormErrors.workspaceName) {
                      setPlanFormErrors((prev) => ({ ...prev, workspaceName: undefined }));
                    }
                  }}
                  maxLength={255}
                  placeholder={t('customerPreview.workspaceNamePlaceholder', 'Enter workspace name')}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'customerPreview.workspaceNameHelper',
                    'Required when switching from individual to team plan.',
                  )}
                </p>
                {showWorkspaceError && workspaceErrorMessage ? (
                  <p className="text-xs text-destructive">{workspaceErrorMessage}</p>
                ) : null}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handlePlanDialogOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handlePlanChange} disabled={disablePlanSubmit}>
              {changePlan.isPending || selectSubscription.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('customerPreview.changePlanCta', 'Change plan')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}

interface PlanCardProps {
  subscription: ProviderSubscriptionSummary | undefined;
  canSelectSubscription?: boolean;
  isLoading: boolean;
  error?: string;
  onRetry: () => void;
  onChangePlan: () => void;
  onSendPayLink: () => void;
  payLinkPending?: boolean;
}

function PlanCard({
  subscription,
  canSelectSubscription,
  isLoading,
  error,
  onRetry,
  onChangePlan,
  onSendPayLink,
  payLinkPending,
}: PlanCardProps) {
  const { t } = useTranslation();
  const statusLabel = subscription ? getSubscriptionStatusLabel(t, subscription) : '';
  const statusColorClass =
    statusColorClasses[subscription?.statusColor ?? 'secondary'] ?? statusColorClasses.secondary;
  const priceInterval = subscription?.price.billingInterval ? ` / ${subscription.price.billingInterval}` : '';
  const priceCurrency = subscription?.price.currency ?? undefined;
  const currentAmount = subscription?.price.unitAmount ?? null;
  const introAmount = subscription?.price.introAmount ?? currentAmount;
  const recurringAmount = subscription?.price.recurringAmount ?? currentAmount;
  const hasIntroPricing =
    introAmount !== null &&
    recurringAmount !== null &&
    introAmount !== undefined &&
    recurringAmount !== undefined &&
    introAmount !== recurringAmount;
  const hasNextInvoice =
    subscription?.price.nextInvoiceAmount != null ||
    subscription?.price.nextInvoiceAt != null;
  const nextInvoiceAmount = subscription?.price.nextInvoiceAmount ?? currentAmount;
  const nextInvoiceAt =
    subscription?.price.nextInvoiceAt ?? (hasNextInvoice ? subscription?.period.end : null);
  const updatePriceAt = subscription?.price.introEndsAt ?? subscription?.price.nextInvoiceAt;
  const showUpcoming = hasIntroPricing || Boolean(hasNextInvoice);
  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <BadgeDollarSign className="h-5 w-5 text-muted-foreground" />
          <CardTitle>{t('customerPreview.billingPlan', 'Billing plan')}</CardTitle>
        </div>
        <CardDescription>
          {t('customerPreview.billingPlanDescription', 'Current subscription, price, entitlements, and actions.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="grid gap-3 md:grid-cols-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-full md:col-span-2" />
          </div>
        ) : error ? (
            <div className="flex items-center justify-between rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        ) : subscription ? (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {t('customerPreview.planLabel', 'Plan')}
                  </div>
                  <Badge className={`capitalize ${statusColorClass}`}>
                    {statusLabel}
                  </Badge>
                </div>
                <div className="text-xl font-semibold">
                  {subscription.plan?.name ?? t('customerPreview.noPlan', 'No plan selected')}
                </div>
                <div className="text-sm text-muted-foreground">
                  {subscription.price.unitAmount !== null
                    ? `${formatMoney(subscription.price.unitAmount, subscription.price.currency, true)}${subscription.price.billingInterval ? ` / ${subscription.price.billingInterval}` : ''}`
                    : t('customerPreview.noPrice', 'No price')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('customerPreview.period', 'Period')}: {formatDate(subscription.period.start)} →{' '}
                  {formatDate(subscription.period.end)}
                </div>
                {(() => {
                  if (!subscription.trialEndsAt) return null;
                  const trialEnd = new Date(subscription.trialEndsAt);
                  if (Number.isNaN(trialEnd.getTime())) return null;
                  const remainingMs = trialEnd.getTime() - Date.now();
                  const remainingDays = Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
                  return (
                    <div className="text-xs text-muted-foreground">
                      {t('customerPreview.trialRemaining', 'Trial ends in {{count}} days', {
                        count: remainingDays,
                      })}
                    </div>
                  );
                })()}
                {hasIntroPricing ? (
                  <div className="rounded-md border border-muted bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>{t('customerPreview.introPriceLabel', 'Intro price')}</span>
                      <span>
                        {introAmount !== null
                          ? `${formatMoney(introAmount, priceCurrency, true)}${priceInterval}`
                          : '—'}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span>{t('customerPreview.basePriceLabel', 'Base price')}</span>
                      <span>
                        {recurringAmount !== null
                          ? `${formatMoney(recurringAmount, priceCurrency, true)}${priceInterval}`
                          : '—'}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span>{t('customerPreview.basePriceStarts', 'Base price starts')}</span>
                      <span>
                        {updatePriceAt
                          ? formatDate(updatePriceAt)
                          : t('customerPreview.basePriceAfterIntro', 'After intro period')}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2 rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">
                  {t('customerPreview.entitlements', 'Entitlements')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {subscription.entitlements?.seats ? (
                    <Badge variant="outline">
                      {t('customerPreview.entitlementSeats', 'Seats')}{' '}
                      {subscription.entitlements.seats.used ?? '—'}/{subscription.entitlements.seats.included ?? '—'}
                    </Badge>
                  ) : null}
                  {subscription.entitlements?.projects ? (
                    <Badge variant="outline">
                      {t('customerPreview.entitlementProjects', 'Projects')}{' '}
                      {subscription.entitlements.projects.included ?? '—'}
                    </Badge>
                  ) : null}
                  {subscription.entitlements?.storage ? (
                    <Badge variant="outline">
                      {t('customerPreview.entitlementStorage', 'Storage')}{' '}
                      {subscription.entitlements.storage.includedGb ?? '—'} GB
                    </Badge>
                  ) : null}
                  {subscription.entitlements?.supportTier ? (
                    <Badge variant="outline">
                      {t('customerPreview.entitlementSupport', 'Support')}: {subscription.entitlements.supportTier}
                    </Badge>
                  ) : null}
                  {!subscription.entitlements ||
                  Object.keys(subscription.entitlements).length === 0 ||
                  (!subscription.entitlements.seats &&
                    !subscription.entitlements.projects &&
                    !subscription.entitlements.storage &&
                    !subscription.entitlements.supportTier) ? (
                    <span className="text-sm text-muted-foreground">
                      {t('customerPreview.noEntitlements', 'No entitlements provided.')}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('customerPreview.trialEnds', 'Trial ends')}: {formatDate(subscription.trialEndsAt)} •{' '}
                  {t('customerPreview.endsAt', 'Ends at')}: {formatDate(subscription.endsAt)}
                </div>
              </div>
            </div>

            {showUpcoming ? (
              <div className="rounded-lg border p-4">
                <div className="text-sm font-medium">
                  {t('customerPreview.upcomingPhases', 'Upcoming phases')}
                </div>
                <div className="mt-2 space-y-2 text-xs text-muted-foreground">
                  {nextInvoiceAt && nextInvoiceAmount !== null ? (
                    <div className="flex items-center justify-between gap-2">
                      <span>{t('customerPreview.nextInvoice', 'Next invoice')}</span>
                      <span className="text-right">
                        {t('customerPreview.nextInvoiceValue', {
                          amount: formatMoney(nextInvoiceAmount, priceCurrency, true),
                          date: formatDate(nextInvoiceAt),
                        })}
                      </span>
                    </div>
                  ) : null}
                  {hasIntroPricing ? (
                    <div className="flex items-center justify-between gap-2">
                      <span>{t('customerPreview.priceUpdate', 'Update price')}</span>
                      <span className="text-right">
                        {t('customerPreview.priceUpdateValue', {
                          from: formatMoney(introAmount ?? 0, priceCurrency, true),
                          to: formatMoney(recurringAmount ?? 0, priceCurrency, true),
                          interval: priceInterval,
                        })}
                        {updatePriceAt ? ` • ${formatDate(updatePriceAt)}` : ''}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                variant="default"
                className="gap-2"
                onClick={onChangePlan}
                disabled={!subscription.flags.canChangePlan}
              >
                <Gauge className="h-4 w-4" />
                {t('customerPreview.changePlanCta', 'Change plan')}
              </Button>
              <Button
                variant="secondary"
                className="gap-2"
                onClick={onSendPayLink}
                disabled={!subscription.flags.canSendPayLink || payLinkPending}
              >
                {payLinkPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                {t('customerPreview.sendPayLink', 'Send pay link')}
              </Button>
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <p>{t('customerPreview.noSubscription', 'No subscription found for this customer.')}</p>
            {canSelectSubscription ? (
              <Button className="mt-4" onClick={onChangePlan}>
                {t('customerPreview.selectSubscriptionCta', 'Select a subscription')}
              </Button>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface UsageCardProps {
  usageSummary: UsageSummary | undefined;
  isLoading: boolean;
  error?: string;
  onRetry: () => void;
}

function UsageCard({ usageSummary, isLoading, error, onRetry }: UsageCardProps) {
  const { t } = useTranslation();
  const metricsEntries = usageSummary ? Object.entries(usageSummary.metrics) : [];
  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-muted-foreground" />
          <CardTitle>{t('customerPreview.usageOverview', 'Usage overview')}</CardTitle>
        </div>
        <CardDescription>
          {t('customerPreview.usagePeriod', 'Current period usage and limits. Period: {{start}} → {{end}} ({{tz}})', {
            start: formatDate(usageSummary?.period.start),
            end: formatDate(usageSummary?.period.end),
            tz: usageSummary?.period.timezone ?? 'UTC',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-28 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center justify-between rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        ) : metricsEntries.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {metricsEntries.map(([key, metric]) => (
              <UsageMetricTile key={key} name={getMetricLabel(t, key)} metric={metric} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('customerPreview.noUsage', 'No usage recorded for this period.')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UsageMetricTile({ name, metric }: { name: string; metric: UsageMetric }) {
  const { t } = useTranslation();
  const limitLabel =
    metric.limit === null
      ? t('customerPreview.unlimited', 'Unlimited')
      : `${formatNumber(metric.limit)} ${metric.unit}`;
  const percent = metric.percentUsed ?? 0;
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-muted-foreground">{name}</div>
          <div className="text-xl font-semibold">
            {formatNumber(metric.used)} {metric.unit}
          </div>
        </div>
        <Badge className={usageStatusClasses[metric.status] ?? ''} variant="outline">
          {getUsageStatusLabel(t, metric.status)}
        </Badge>
      </div>
      {metric.status !== 'unlimited' ? (
        <div className="space-y-2">
          <Progress value={percent} />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{percent ? `${percent.toFixed(1)}% ${t('customerPreview.used', 'used')}` : `0% ${t('customerPreview.used', 'used')}`}</span>
            <span>
              {t('customerPreview.limit', 'Limit')}: {limitLabel}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {t('customerPreview.unlimitedUsage', 'Unlimited usage')}
        </div>
      )}
    </div>
  );
}

interface UsageRecordsTableProps {
  usageSummary: UsageSummary | undefined;
  usageRecords: ReturnType<typeof usePreviewUsageRecords>['data'];
  isLoading: boolean;
  metricFilter: string | undefined;
  onMetricChange: (value: string | undefined) => void;
  page: number;
  onPageChange: (page: number) => void;
  availableMetrics: string[];
}

function UsageRecordsTable({
  usageSummary,
  usageRecords,
  isLoading,
  metricFilter,
  onMetricChange,
  page,
  onPageChange,
  availableMetrics,
}: UsageRecordsTableProps) {
  const { t } = useTranslation();
  const meta = usageRecords?.meta;
  const canPrev = (meta?.currentPage ?? page) > 1;
  const canNext = meta?.lastPage ? (meta.currentPage ?? page) < meta.lastPage : (usageRecords?.data?.length ?? 0) === 10;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>{t('customerPreview.usageRecords', 'Usage records')}</CardTitle>
              <CardDescription>
                {t('customerPreview.usageRecordsDescription', 'Recent usage events for this period.')}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={metricFilter ?? 'all'}
              onValueChange={(value) => onMetricChange(value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('customerPreview.metricFilter', 'Filter metric')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('customerPreview.allMetrics', 'All metrics')}</SelectItem>
                {availableMetrics.map((metric) => (
                  <SelectItem key={metric} value={metric}>
                    {getMetricLabel(t, metric)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : usageRecords?.data?.length ? (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">{t('customerPreview.recordedAt', 'Recorded at')}</TableHead>
                    <TableHead>{t('customerPreview.metric', 'Metric')}</TableHead>
                    <TableHead>{t('customerPreview.endpoint', 'Endpoint')}</TableHead>
                    <TableHead className="text-right">{t('customerPreview.quantity', 'Quantity')}</TableHead>
                    <TableHead className="text-right">{t('customerPreview.credits', 'Credits')}</TableHead>
                    <TableHead className="text-right">{t('customerPreview.cost', 'Cost')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usageRecords.data.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{formatDate(record.recordedAt)}</TableCell>
                      <TableCell>{getMetricLabel(t, record.usageType)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {record.endpointPath ?? record.endpointId ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(record.quantity)}</TableCell>
                      <TableCell className="text-right">{formatNumber(record.creditsConsumed)}</TableCell>
                      <TableCell className="text-right">
                        {record.costAmount !== null && record.costAmount !== undefined
                          ? formatMoney(record.costAmount, record.currency ?? 'USD')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div>
                {t('customerPreview.pageXofY', 'Page {{page}}{{of}}', {
                  page: meta?.currentPage ?? page,
                  of: meta?.lastPage ? ` ${t('customerPreview.of', 'of')} ${meta.lastPage}` : '',
                })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => onPageChange(Math.max(1, (meta?.currentPage ?? page) - 1))}>
                  {t('customerPreview.prev', 'Prev')}
                </Button>
                <Button size="sm" variant="outline" disabled={!canNext} onClick={() => onPageChange((meta?.currentPage ?? page) + 1)}>
                  {t('customerPreview.next', 'Next')}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {usageSummary
              ? t('customerPreview.noUsageFiltered', 'No usage records for the selected filters.')
              : t('customerPreview.noUsageRecords', 'No usage records available.')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface InvoicesCardProps {
  invoices: InvoiceRecord[] | undefined;
  isLoading: boolean;
}

function InvoicesCard({ invoices, isLoading }: InvoicesCardProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-muted-foreground" />
          {t('customerPreview.recentInvoices', 'Recent invoices')}
        </CardTitle>
        <CardDescription>
          {t('customerPreview.invoicesDescription', 'Latest invoices synced from billing.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : invoices && invoices.length > 0 ? (
          invoices.map((invoice) => (
            <div key={invoice.id} className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">
                  {t('customerPreview.invoice', 'Invoice')} {invoice.id}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('customerPreview.issued', 'Issued')} {formatDate(invoice.issuedAt)} • {invoice.status}
                </div>
              </div>
              <div className="text-right text-sm font-semibold">
                {formatMoney(invoice.amountDue, invoice.currency)}
              </div>
            </div>
          ))
        ) : (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t('customerPreview.noInvoices', 'No invoices available.')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethodSummary | undefined;
  isLoading: boolean;
}

function PaymentMethodCard({ paymentMethod, isLoading }: PaymentMethodCardProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          {t('customerPreview.paymentMethod', 'Payment method')}
        </CardTitle>
        <CardDescription>
          {t('customerPreview.paymentMethodDescription', 'Default payment method on file.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : paymentMethod?.status === 'default' ? (
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium capitalize">
                {paymentMethod.brand ?? t('customerPreview.card', 'Card')}
              </div>
              <Badge variant="outline" className="gap-1">
                <Wallet className="h-3 w-3" />
                {t('customerPreview.default', 'Default')}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              •••• {paymentMethod.last4 ?? '—'} • {t('customerPreview.expires', 'Expires')}{' '}
              {paymentMethod.expMonth}/{paymentMethod.expYear}
            </div>
            {paymentMethod.funding ? (
              <div className="text-xs text-muted-foreground">
                {t('customerPreview.funding', 'Funding')}: {paymentMethod.funding}
              </div>
            ) : null}
          </div>
        ) : paymentMethod?.status === 'none' ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            {t('customerPreview.noPaymentMethod', 'No payment method on file.')}
          </div>
        ) : paymentMethod?.status === 'error' ? (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {paymentMethod.message ?? t('customerPreview.paymentUnavailable', 'Payment method unavailable.')}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            {t('customerPreview.paymentUnavailable', 'Payment method unavailable.')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InfoDotIcon() {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border">
      <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
    </span>
  );
}
